import { LoadingContainer, Breadcrumb, Panels, Bar } from 'peach/components'
import { getName } from 'peach/helpers'
import { useHistory, useParams, useRouteMatch } from 'peach/router'
import { useQueryClient } from '@tanstack/react-query'

import { Clickable, ConfirmDialog, DynamicForm, Page } from 'core/components'
import {
  useInternalCompanyApiKeyGet,
  useInternalCompanyApiKeyUpdate,
  useInternalCompanyApiKeyDelete,
} from 'core/types/generated/api-keys/hooks'
import type { RequestBodyAPIKeyUpdateAPIVersioning } from 'core/types/generated/api-keys/types'

const APIKey = () => {
  const history = useHistory()
  const { url } = useRouteMatch()
  const { companyId, apiKeyId } = useParams<{
    companyId?: string
    apiKeyId?: string
  }>()

  const queryClient = useQueryClient()

  const {
    data: apiKey,
    isLoading,
    refetch,
  } = useInternalCompanyApiKeyGet({
    companyId,
    keyId: apiKeyId,
  })

  const { mutateAsync: updateApiKey } = useInternalCompanyApiKeyUpdate({
    companyId,
    keyId: apiKeyId,
  })

  const { mutateAsync: deleteApiKey } = useInternalCompanyApiKeyDelete({
    companyId,
    keyId: apiKeyId,
  })

  const onDelete = async () => {
    await deleteApiKey()
    void queryClient.invalidateQueries({
      queryKey: ['companies', companyId, 'keys'],
    })
    history.push(`/companies/${companyId}/api-keys`)
  }

  const onSubmit = async (payload: any) => {
    await updateApiKey(payload as RequestBodyAPIKeyUpdateAPIVersioning)
    await refetch()
  }

  const title = getName(apiKey) || apiKey?.description || 'API Key'

  return (
    <Panels header={<Bar title={title} />}>
      <LoadingContainer isLoading={isLoading}>
        <Breadcrumb title={title} to={url} />

        <Page variant='full'>
          <DynamicForm
            data={apiKey || {}}
            fieldEditMode='toggle'
            fieldOverrides={{
              whitelistedIPs: 'JsonInput',
            }}
            omitFields={[
              'id',
              'companyId',
              'userId',
              'key',
              'status',
              'createdAt',
              'updatedAt',
              'deletedAt',
              'apiVersionUpdatedAt',
              'apiVersionDowngradeNotAllowedAfter',
            ]}
            onSubmit={onSubmit}
            otherActions={[
              <ConfirmDialog
                key='delete-action'
                onConfirm={onDelete}
                title='Delete API Key'
              >
                <Clickable variant='outline'>Delete</Clickable>
              </ConfirmDialog>,
            ]}
          />
        </Page>
      </LoadingContainer>
    </Panels>
  )
}

export default APIKey
